// Version 1.1
import React from "react";

export default function BuildNotes({ onClose }) {
  return (
    <div className="min-h-screen bg-white text-black px-4 py-10 max-w-4xl mx-auto font-sans">
      <div className="mb-6 text-right">
        <p className="text-sm uppercase tracking-widest text-gray-500">
          ALWAYS NOTICING
        </p>
        <p className="text-xs uppercase tracking-wider text-custom-red">
          LATEST CREATIVE PROJECT
        </p>
      </div>

      <button
        onClick={onClose}
        className="mb-6 text-sm text-custom-red hover:underline font-medium uppercase"
      >
        &larr; Back to Tarot Reading
      </button>

      <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 lowercase text-left">
        🧠 How I Built READ ME TAROT
      </h1>
      <p className="text-lg md:text-xl text-gray-600 mb-8">
        A tech journey into AI, code, and a little bit of magic.
      </p>

      <p className="mb-6 text-gray-700 leading-relaxed">
        Bringing READ ME TAROT to life wasn't about building the most
        technically complex app — it was about using the right tools in the
        correct sequence to make something soulful, functional, and shareable.
        Here's the journey I took, tool by tool:
      </p>

      <hr className="my-8 border-gray-300" />

      <div>
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">
          Technical Outline
        </h2>
        <div className="space-y-4 text-gray-700 leading-relaxed">
          <div>
            <h3 className="font-semibold text-gray-800">Frontend Framework</h3>
            <ul className="list-disc list-inside ml-4">
              <li>
                <strong>React</strong>: Core library for building the user
                interface.
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Styling</h3>
            <ul className="list-disc list-inside ml-4">
              <li>
                <strong>Tailwind CSS</strong>: Utility-first CSS framework for
                styling components.
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Animation</h3>
            <ul className="list-disc list-inside ml-4">
              <li>
                <strong>Framer Motion</strong>: Library for creating animations
                and transitions.
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">State Management</h3>
            <ul className="list-disc list-inside ml-4">
              <li>
                <strong>Zustand</strong>: Minimalist state management library
                for React.
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">
              Build Tool & Development Server
            </h3>
            <ul className="list-disc list-inside ml-4">
              <li>
                <strong>Webpack</strong> (as per terminal output): Tool for
                bundling code, running a local dev server, and creating
                production builds.{" "}
                <em>
                  (Note: Project context mentioned Vite, confirming which is
                  primary might be useful).
                </em>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Backend & AI</h3>
            <ul className="list-disc list-inside ml-4">
              <li>
                <strong>OpenAI APIs</strong>: Powers AI-driven tarot readings.
              </li>
              <li>
                <strong>Vercel / Render Serverless Functions</strong>: Hosts
                backend logic communicating with OpenAI.
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">
              Development Workflow & Tools
            </h3>
            <ul className="list-disc list-inside ml-4">
              <li>
                <strong>Node.js / npm</strong>: JavaScript runtime and package
                manager.
              </li>
              <li>
                <strong>Cursor</strong>: AI-powered code editor for rapid
                iteration.
              </li>
              <li>
                <strong>GitHub</strong>: Version control and code hosting.
              </li>
              <li>
                <strong>Vercel / Render</strong>: Platforms for deployment and
                hosting.
              </li>
            </ul>
          </div>
        </div>
      </div>

      <hr className="my-8 border-gray-300" />

      <div className="space-y-8">
        <div>
          <h2 className="text-xl font-semibold mb-3 text-gray-800">
            ✨ ChatGPT (hi, that's you!)
          </h2>
          <p className="mb-3 text-gray-700 leading-relaxed">
            ChatGPT was my brainstorming partner, code mentor, UI copilot, and
            creative collaborator. From writing tarot card meanings to debugging
            server routes, this project wouldn't exist without it. I used it to:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3 ml-4">
            <li>Develop dynamic reading summaries</li>
            <li>Refactor code</li>
            <li>Create Tarot copy that feels alive</li>
            <li>Build out the app architecture step-by-step in Cursor</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-3 text-gray-800">
            💻 Cursor (my coding cockpit)
          </h2>
          <p className="mb-3 text-gray-700 leading-relaxed">
            Cursor is a developer-focused code editor built on top of VS Code
            with native AI integration. It made it incredibly easy to iterate
            fast — I could prompt changes, refactor files, and keep momentum
            while staying in flow. I used Cursor for:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3 ml-4">
            <li>File-based AI commands</li>
            <li>Git commits and debugging</li>
            <li>
              Navigating JSX-heavy structures like my card reader components
            </li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-3 text-gray-800">
            🧪 Vercel (my first staging ground)
          </h2>
          <p className="mb-3 text-gray-700 leading-relaxed">
            Vercel was where I first tested the live app, but I eventually hit
            limitations around serverless routing and API security. It's amazing
            for frontend hosting and rapid deployment, but less ideal for custom
            Node.js backends with OpenAI API keys. Still — a great starting
            point.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-3 text-gray-800">
            🎨 Google Labs ImageFX (Tarot visual concepts)
          </h2>
          <p className="mb-3 text-gray-700 leading-relaxed">
            ImageFX from Google Labs helped bring some early visual magic to
            life. I used it to explore symbol sets, card aesthetics, and mood
            boards that inspired the final UI direction—AI-generated art as
            ideation — not final assets, but deeply useful.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-3 text-gray-800">
            🔗 GitHub (version control + clean repo vibes)
          </h2>
          <p className="mb-3 text-gray-700 leading-relaxed">
            GitHub kept my project organized and clean as I moved from
            prototyping to polish. After reworking the directory structure and
            removing Vercel-specific artifacts, I ended up with a streamlined,
            Render-ready app that's easy to build on.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-3 text-gray-800">
            🚀 Render (final host + backend engine)
          </h2>
          <p className="mb-3 text-gray-700 leading-relaxed">
            Render is where the app now lives — with a proper server.js handling
            API routes securely and serving the React frontend. It was the
            perfect fit once I needed full control over hosting, routing, and
            environment variables like OPENAI_API_KEY.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-3 text-gray-800">
            🔮 Bonus Tools in the Mix:
          </h2>
          <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3 ml-4">
            <li>
              <strong>html2canvas</strong> – for generating story-friendly
              snapshots of card pulls
            </li>
            <li>
              <strong>Tailwind CSS</strong> – for fast, flexible UI styling
            </li>
            <li>
              <strong>OpenAI's GPT-4 API</strong> – to generate custom reading
              summaries in real-time
            </li>
          </ul>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-3 text-gray-800">
            🔁 TL;DR
          </h2>
          <p className="mb-3 text-gray-700 leading-relaxed">
            READ ME TAROT was built with a mix of AI-powered tools, a flexible
            dev environment, and a ton of iteration. It's a testament to what's
            possible when you combine intuition with infrastructure — and trust
            the pull.
          </p>
          <p className="mb-6 text-gray-700 leading-relaxed">
            If you're curious to build your own tool, I'd say this: start
            scrappy, stay curious, and ask good questions. The rest will follow.
          </p>
        </div>
      </div>

      <hr className="my-8 border-gray-300" />

      <div className="flex justify-center mt-8">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="text-sm text-gray-600 hover:text-custom-red hover:underline font-medium uppercase"
        >
          &uarr; Back to Top
        </button>
      </div>
    </div>
  );
}
